"use client";

import { useEffect, useRef, useState } from "react";

import { Icon } from "@calcom/ui/components/icon";

type FormFieldDef = {
  id: number;
  fieldType: string;
  label: string;
  description: string | null;
  placeholder: string | null;
  options: any;
  required: boolean;
  settings: any;
};

type FormDef = {
  assignmentId: number;
  formId: number;
  formName: string;
  formDescription: string | null;
  required: boolean;
  requiresSignature?: boolean;
  source: "form" | "zignature";
  fields: FormFieldDef[];
};

type FieldResponse = {
  fieldId: number;
  value: string | boolean | string[];
  fileUrl?: string;
  signatureData?: string;
};
type FormResponses = { formId: number; fieldResponses: FieldResponse[] };

type Props = {
  eventTypeId: number;
  onResponsesChange: (responses: FormResponses[]) => void;
  onValidationError: (hasError: boolean) => void;
};

const STORAGE_KEY = (id: number) => `schedly-connect-forms-${id}`;

function SignatureField({ value, onChange, error }: { value: string; onChange: (v: string) => void; error: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx?.clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <div className={`overflow-hidden rounded-lg border ${error ? "border-error" : "border-default"}`}>
      <canvas
        ref={canvasRef}
        width={460}
        height={120}
        className="bg-default w-full cursor-crosshair touch-none"
        style={{ touchAction: "none" }}
        onPointerDown={(e) => {
          const canvas = canvasRef.current;
          const ctx = canvas?.getContext("2d");
          if (!ctx || !canvas) return;
          canvas.setPointerCapture(e.pointerId);
          const rect = canvas.getBoundingClientRect();
          const scaleX = canvas.width / rect.width;
          const scaleY = canvas.height / rect.height;
          ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue("--cal-text").trim() || "#111";
          ctx.lineWidth = 1.5;
          ctx.lineCap = "round";
          ctx.lineJoin = "round";
          ctx.beginPath();
          ctx.moveTo((e.clientX - rect.left) * scaleX, (e.clientY - rect.top) * scaleY);
          const onMove = (me: PointerEvent) => {
            ctx.lineTo((me.clientX - rect.left) * scaleX, (me.clientY - rect.top) * scaleY);
            ctx.stroke();
          };
          const onUp = () => {
            canvas.removeEventListener("pointermove", onMove);
            canvas.removeEventListener("pointerup", onUp);
            onChange(canvas.toDataURL());
          };
          canvas.addEventListener("pointermove", onMove);
          canvas.addEventListener("pointerup", onUp);
        }}
      />
      <div className="border-t flex items-center justify-between px-3 py-1.5" style={{ borderColor: "var(--cal-border)" }}>
        <span className="text-muted text-xs">Draw your signature above</span>
        <button type="button" className="text-muted hover:text-emphasis text-xs underline transition-colors" onClick={clear}>
          Clear
        </button>
      </div>
    </div>
  );
}

function FileField({
  value,
  onChange,
  error,
  placeholder,
  eventTypeId,
}: {
  value: string;
  onChange: (v: string) => void;
  error: boolean;
  placeholder?: string | null;
  eventTypeId: number;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // value format after upload: "file::{name}::{url}"
  const isUploaded = value.startsWith("file::");
  const parts = isUploaded ? value.split("::") : [];
  const displayName = isUploaded ? parts[1] : "";
  const fileUrl = isUploaded ? parts.slice(2).join("::") : "";

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadError(null);

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
      });

      const res = await fetch("/api/services/forms/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: dataUrl, filename: file.name, type: "file", eventTypeId }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Upload failed");
      }

      const { url } = await res.json();
      onChange(`file::${file.name}::${url}`);
    } catch (err: any) {
      setUploadError(err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      e.target.value = "";
    }
  };

  return (
    <div>
      <label
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-5 transition-colors ${
          uploading
            ? "border-default bg-subtle cursor-not-allowed"
            : error
            ? "border-error bg-error/5"
            : isUploaded
            ? "border-emphasis bg-subtle"
            : "border-default bg-default hover:border-emphasis hover:bg-subtle"
        }`}>
        <input
          type="file"
          className="sr-only"
          onChange={handleFileSelect}
          disabled={uploading}
          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
        />
        {uploading ? (
          <div className="flex items-center gap-2 text-sm">
            <div className="border-emphasis h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
            <span className="text-muted">Uploading…</span>
          </div>
        ) : isUploaded ? (
          <div className="flex items-center gap-2 text-sm">
            <Icon name="file" className="text-emphasis h-4 w-4 shrink-0" />
            <span className="text-emphasis max-w-[200px] truncate font-medium">{displayName}</span>
            <span className="text-muted text-xs">✓ uploaded</span>
          </div>
        ) : (
          <>
            <Icon name="upload" className="text-muted h-5 w-5" />
            <div className="text-center">
              <span className="text-emphasis text-sm font-medium">Click to upload</span>
              <p className="text-muted mt-0.5 text-xs">
                {placeholder || "PDF, JPG, PNG, DOC, TXT (max 5 MB)"}
              </p>
            </div>
          </>
        )}
      </label>
      {isUploaded && (
        <div className="mt-1 flex items-center justify-between">
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-indigo-600 hover:underline dark:text-indigo-400">
            Preview file ↗
          </a>
          <button
            type="button"
            className="text-muted hover:text-emphasis text-xs underline transition-colors"
            onClick={() => onChange("")}>
            Remove
          </button>
        </div>
      )}
      {uploadError && <p className="mt-1 text-xs text-red-500">{uploadError}</p>}
    </div>
  );
}

function renderField(
  field: FormFieldDef,
  value: string,
  onChange: (v: string) => void,
  error: boolean,
  eventTypeId: number
) {
  const baseInput =
    "w-full rounded-lg border px-3 py-2 text-sm text-default placeholder:text-muted transition-colors focus:outline-none focus:ring-0 focus:border-emphasis hover:border-emphasis";
  const borderClass = error
    ? "border-error bg-error/10"
    : "border-default bg-default";

  switch (field.fieldType) {
    case "textarea":
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          rows={3}
          className={`${baseInput} ${borderClass}`}
        />
      );
    case "select": {
      const opts: string[] = Array.isArray(field.options) ? field.options : [];
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${baseInput} ${borderClass}`}>
          <option value="">Select an option…</option>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    }
    case "radio": {
      const opts: string[] = Array.isArray(field.options) ? field.options : [];
      return (
        <div className="space-y-1.5">
          {opts.map((o) => (
            <label key={o} className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="radio"
                value={o}
                checked={value === o}
                onChange={() => onChange(o)}
                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-default">{o}</span>
            </label>
          ))}
        </div>
      );
    }
    case "checkbox":
      return (
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={value === "true"}
            onChange={(e) => onChange(e.target.checked ? "true" : "false")}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-default">{field.label}</span>
        </label>
      );
    case "date":
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${baseInput} ${borderClass}`}
        />
      );
    case "number":
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className={`${baseInput} ${borderClass}`}
        />
      );
    case "email":
      return (
        <input
          type="email"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className={`${baseInput} ${borderClass}`}
        />
      );
    case "phone":
      return (
        <input
          type="tel"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className={`${baseInput} ${borderClass}`}
        />
      );
    case "time":
      return (
        <input
          type="time"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${baseInput} ${borderClass}`}
        />
      );
    case "file":
      return <FileField value={value} onChange={onChange} error={error} placeholder={field.placeholder} eventTypeId={eventTypeId} />;
    case "signature":
      return <SignatureField value={value} onChange={onChange} error={error} />;
    case "section":
      return null;
    case "label":
      return null;
    case "text":
    default:
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          className={`${baseInput} ${borderClass}`}
        />
      );
  }
}

function PdfFormNotice({ form }: { form: FormDef }) {
  const needsSignature = form.requiresSignature ?? false;
  return (
    <div
      className="overflow-hidden rounded-xl border"
      style={{ borderColor: "var(--cal-border-subtle)" }}>
      {/* Header */}
      <div
        className="border-b px-4 py-3"
        style={{ background: "var(--cal-bg-subtle, #f9fafb)", borderColor: "var(--cal-border-subtle)" }}>
        <div className="flex items-center gap-2">
          <Icon name={needsSignature ? "pen-line" : "file-text"} className="h-4 w-4 text-violet-500" />
          <span className="text-emphasis text-sm font-semibold">{form.formName}</span>
          {needsSignature ? (
            <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">
              Signature Required
            </span>
          ) : form.required ? (
            <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
              Required
            </span>
          ) : null}
        </div>
      </div>
      {/* Body */}
      <div
        className="flex items-start gap-3 px-4 py-3"
        style={{ background: "var(--cal-bg, #fff)" }}>
        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-violet-50 dark:bg-violet-900/20">
          <Icon name="send" className="h-4 w-4 text-violet-500" />
        </div>
        <div>
          <p className="text-emphasis text-sm font-medium">
            {needsSignature ? "Document signing required" : "Document required"}
          </p>
          <p className="text-subtle mt-0.5 text-xs">
            {form.formDescription}
          </p>
        </div>
      </div>
    </div>
  );
}

export function ConnectIntakeForms({ eventTypeId, onResponsesChange, onValidationError }: Props) {
  const [forms, setForms] = useState<FormDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [values, setValues] = useState<Record<string, Record<number, string>>>({});
  const [touched, setTouched] = useState<Record<string, Record<number, boolean>>>({});

  useEffect(() => {
    fetch(`/api/forms/public-connect?eventTypeId=${eventTypeId}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data: FormDef[]) => {
        setForms(data);
        // Restore saved values from sessionStorage (only for regular form-builder forms)
        const saved = sessionStorage.getItem(STORAGE_KEY(eventTypeId));
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            setValues(parsed);
          } catch {}
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [eventTypeId]);

  useEffect(() => {
    // Only regular forms participate in responses / validation
    const regularForms = forms.filter((f) => f.source !== "zignature");

    if (regularForms.length === 0) {
      onResponsesChange([]);
      onValidationError(false);
      if (forms.length > 0) {
        // PDF-only: nothing to persist for submitter
        sessionStorage.removeItem(STORAGE_KEY(eventTypeId));
        sessionStorage.removeItem(`schedly-forms-meta-${eventTypeId}`);
      }
      return;
    }

    const responses: FormResponses[] = regularForms.map((form) => ({
      formId: form.formId,
      fieldResponses: form.fields
        .filter((f) => f.fieldType !== "section" && f.fieldType !== "label")
        .map((f): FieldResponse => {
          const rawVal = values[`f${form.formId}`]?.[f.id] || "";

          if (f.fieldType === "file" && rawVal.startsWith("file::")) {
            const parts = rawVal.split("::");
            const name = parts[1] || "";
            const url = parts.slice(2).join("::");
            return { fieldId: f.id, value: name, fileUrl: url };
          }

          if (f.fieldType === "signature" && rawVal.startsWith("data:")) {
            return { fieldId: f.id, value: "signed", signatureData: rawVal };
          }

          return { fieldId: f.id, value: rawVal };
        })
        .filter((r) => {
          const v = String(r.value || "");
          return v !== "" || r.fileUrl || r.signatureData;
        }),
    }));

    // Save to sessionStorage for the confirmation page submitter
    sessionStorage.setItem(STORAGE_KEY(eventTypeId), JSON.stringify(values));
    sessionStorage.setItem(`schedly-forms-meta-${eventTypeId}`, JSON.stringify({ eventTypeId, responses }));

    onResponsesChange(responses);

    // Validate required fields
    let hasError = false;
    for (const form of regularForms) {
      for (const field of form.fields) {
        if (field.required && field.fieldType !== "section" && field.fieldType !== "label") {
          const val = values[`f${form.formId}`]?.[field.id] || "";
          if (!val || val === "false") {
            hasError = true;
            break;
          }
        }
      }
      if (hasError) break;
    }
    onValidationError(hasError);
  }, [values, forms]);

  function setFieldValue(formId: number, fieldId: number, val: string) {
    const key = `f${formId}`;
    setValues((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [fieldId]: val },
    }));
    setTouched((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [fieldId]: true },
    }));
  }

  if (loading || forms.length === 0) return null;

  return (
    <div className="mt-4 space-y-5">
      <div className="flex items-center gap-2">
        <div className="h-px flex-1 bg-subtle" />
        <span className="flex items-center gap-1.5 text-xs font-medium text-muted">
          <Icon name="clipboard" className="h-3.5 w-3.5" />
          Additional Information
        </span>
        <div className="h-px flex-1 bg-subtle" />
      </div>

      {forms.map((form) => {
        // PDF / Zignature forms: show notice only, no fields to fill
        if (form.source === "zignature") {
          return <PdfFormNotice key={`pdf-${form.formId}`} form={form} />;
        }

        return (
          <div
            key={form.formId}
            className="overflow-hidden rounded-xl border"
            style={{ borderColor: "var(--cal-border-subtle)" }}>
            {/* Form header */}
            <div
              className="border-b px-4 py-3"
              style={{ background: "var(--cal-bg-subtle, #f9fafb)", borderColor: "var(--cal-border-subtle)" }}>
              <div className="flex items-center gap-2">
                <Icon name="clipboard-list" className="h-4 w-4 text-indigo-500" />
                <span className="text-emphasis text-sm font-semibold">{form.formName}</span>
                {form.required && (
                  <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                    Required
                  </span>
                )}
              </div>
              {form.formDescription && (
                <p className="text-subtle mt-0.5 text-xs">{form.formDescription}</p>
              )}
            </div>

            {/* Fields */}
            <div
              className="divide-y px-4"
              style={{ background: "var(--cal-bg, #fff)", divideColor: "var(--cal-border-subtle)" }}>
              {form.fields.map((field) => {
                if (field.fieldType === "section") {
                  return (
                    <div key={field.id} className="py-3">
                      <h4 className="text-emphasis text-sm font-semibold">{field.label}</h4>
                      {field.description && <p className="text-subtle text-xs">{field.description}</p>}
                    </div>
                  );
                }
                if (field.fieldType === "label") {
                  return (
                    <div key={field.id} className="py-3">
                      {field.description && <p className="text-subtle text-sm">{field.description}</p>}
                    </div>
                  );
                }

                const val = values[`f${form.formId}`]?.[field.id] || "";
                const isTouched = touched[`f${form.formId}`]?.[field.id] || false;
                const isError = isTouched && field.required && !val;

                return (
                  <div key={field.id} className="py-3">
                    <label
                      className="mb-1.5 block text-sm font-medium"
                      style={{ color: "var(--cal-text-emphasis)" }}>
                      {field.label}
                      {field.required && <span className="ml-0.5 text-red-500">*</span>}
                    </label>
                    {field.description && (
                      <p className="text-subtle mb-1.5 text-xs">{field.description}</p>
                    )}
                    {renderField(field, val, (v) => setFieldValue(form.formId, field.id, v), isError, eventTypeId)}
                    {isError && <p className="mt-1 text-xs text-red-500">This field is required</p>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
